import React, { useEffect, useState } from 'react';

import { Grid, MenuItem } from '@mui/material';

import LoadingComponent from '@/components/templates/LoadingComponent';
import useEnterpriseService from '@/services/enterprise';
import useReportIndicatorValuesService from '@/services/report-indicator-values';

import DynamicChart from './components/DynamicChart';
import MetricCard from './components/MetricCard';
import { GridFilter, ContainerFilter, FilterButton, CleanButton, Div, TextFildStyled } from '../../components/ConsultantDateFilter/style';
import { Section, PaperTitle } from './styles';

const Goals = ({ idEnterprise, currentUserRole }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [filterYear, setFilterYear] = useState(null);
    const [filterProperty, setFilterProperty] = useState(null);
    const [filterConsultant, setFilterConsultant] = useState(null);
    const [averagesData, setAveragesData] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [properties, setProperties] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [goals, setGoals] = useState({});
    const [chartsData, setChartsData] = useState([]);
    const [isChartLoading, setIsChartLoading] = useState(false);

    const reportIndicatorValuesService = useReportIndicatorValuesService();
    const enterpriseService = useEnterpriseService();

    const fetchResources = async () => {
        try {
            const response = await reportIndicatorValuesService.getDashboardResources(idEnterprise);
            const { properties, years, goals } = response.data;

            setProperties(properties || []);
            setAvailableYears(years || []);
            setGoals(goals || {});

            // Definir o ano padrão como o mais recente disponível
            if (years && years.length > 0) {
                setFilterYear(years[0]);
            }
        } catch (error) {
            console.error('Erro ao buscar recursos do dashboard:', error);
        }
    };

    const fetchChartData = async () => {
        setIsChartLoading(true);
        setChartsData([]);

        try {
            const params = {
                enterprise_id: idEnterprise,
                ...(filterYear && { year: filterYear }),
                ...(filterProperty && { property_id: filterProperty }),
                ...(filterConsultant && { consultant_id: filterConsultant }),
                ...(currentUserRole && { current_user_role: currentUserRole })
            };

            const response = await reportIndicatorValuesService.getDashboardCharts(params);
            // Agora response.data é um array de gráficos
            setChartsData(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar dados dos gráficos:', error);
            setChartsData([]);
        } finally {
            setIsChartLoading(false);
        }
    };

    const fetchConsultants = async () => {
        try {
            const response = await enterpriseService.getEnterpriseConsultats(idEnterprise, currentUserRole || 4);
            setConsultants(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar consultores:', error);
        }
    };

    const fetchAverages = async () => {
        setIsLoading(true);
        try {
            const params = {
                enterprise_id: idEnterprise,
                ...(filterYear && { year: filterYear }),
                ...(filterProperty && { property_id: filterProperty }),
                ...(filterConsultant && { consultant_id: filterConsultant }),
                ...(currentUserRole && { current_user_role: currentUserRole })
            };

            const response = await reportIndicatorValuesService.getDashboardAverages(params);
            setAveragesData(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar médias dos indicadores:', error);
            setAveragesData([]);
        } finally {
            setIsLoading(false);
            setIsInitialLoading(false);
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            if (idEnterprise) {
                setIsInitialLoading(true);
                try {
                    await Promise.all([fetchResources(), fetchConsultants()]);
                } catch (error) {
                    console.error('Erro ao carregar dados iniciais:', error);
                } finally {
                    setIsInitialLoading(false);
                }
            }
        };
        loadInitialData();
    }, [idEnterprise]);

    useEffect(() => {
        if (filterYear) {
            fetchAverages();
            fetchChartData();
        }
    }, [filterYear]);

    const formatValue = (value, indicatorId) => {
        if (!value || value === 0) return '0';

        const numericValue = Number(String(value));

        // IDs 3 (CCS) e 4 (CBT/CPP) devem ser arredondados (número inteiro)
        if (indicatorId === 3 || indicatorId === 4) {
            return Math.round(numericValue).toString();
        }

        // Demais indicadores: usar vírgula no lugar de ponto
        return numericValue.toFixed(2).replace('.', ',');
    };

    const getIndicatorData = (indicatorData) => {
        const indicatorId = indicatorData.report_indicator_id;
        const hasData = indicatorData.average !== null && indicatorData.average !== undefined;
        const rawValue = hasData ? indicatorData.average : 0;
        const formattedValue = hasData ? formatValue(rawValue, indicatorId) : 'Sem dados';

        // Buscar meta do indicador
        const goalValue = goals[indicatorId];
        const formattedGoal = goalValue ? formatValue(goalValue, indicatorId) : null;

        // Calcular percentual em relação à meta
        let percentage = null;
        let isAboveGoal = null;
        if (hasData && goalValue && goalValue !== 0 && rawValue !== 0) {
            percentage = ((rawValue - goalValue) / goalValue) * 100;

            // Verificar orientação da meta (higher_better ou lower_better)
            const goalOrientation = indicatorData.goal_orientation;

            // Só calcular isAboveGoal se goal_orientation não for null
            if (goalOrientation === 'lower_better') {
                // Para indicadores onde menor é melhor (ex: CCS, mortalidade)
                isAboveGoal = rawValue <= goalValue; // Verde se abaixo/igual, vermelho se acima
            } else if (goalOrientation === 'higher_better') {
                // Para indicadores onde maior é melhor
                isAboveGoal = rawValue >= goalValue; // Verde se acima/igual, vermelho se abaixo
            }
            // Se goalOrientation for null, isAboveGoal permanece null e a seta não será exibida
        }

        return {
            value: formattedValue,
            goal: formattedGoal,
            percentage,
            isAboveGoal,
            name: `Média ${indicatorData.indicator_name}`,
            unit: hasData && indicatorData.indicator_measure ? ` ${indicatorData.indicator_measure}` : ''
        };
    };

    const handleFilterClick = () => {
        fetchAverages();
        fetchChartData();
    };

    const handleClean = () => {
        // Resetar para o ano mais recente disponível
        if (availableYears && availableYears.length > 0) {
            setFilterYear(availableYears[0]);
        }
        setFilterProperty(null);
        setFilterConsultant(null);
    };

    // Mostrar loading enquanto carrega dados iniciais
    if (isInitialLoading) {
        return <LoadingComponent hideLogo />;
    }

    // Mostrar mensagem se não houver dados de anos disponíveis
    if (!availableYears || availableYears.length === 0) {
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '400px' }}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <h2>Nenhum dado disponível</h2>
                    <p style={{ color: '#666', marginTop: '1rem' }}>Não há indicadores salvos em relatórios para esta empresa ainda.</p>
                    <p style={{ color: '#666' }}>Os dados aparecerão aqui assim que relatórios com indicadores forem criados.</p>
                </Grid>
            </Grid>
        );
    }

    return (
        <>
            {/* Filtros */}
            <Grid container>
                <GridFilter item xs={12} marginBottom="2rem">
                    <ContainerFilter>
                        <Grid item xs={2}>
                            <p>Ano</p>
                            <Div width="100%">
                                <TextFildStyled
                                    fullWidth
                                    size="small"
                                    select
                                    value={filterYear || ''}
                                    onChange={(e) => setFilterYear(e.target.value)}
                                >
                                    {availableYears?.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </TextFildStyled>
                            </Div>
                        </Grid>
                        <Grid item marginLeft={4} xs={2}>
                            <p>Propriedade</p>
                            <Div width="100%">
                                <TextFildStyled
                                    fullWidth
                                    size="small"
                                    select
                                    value={filterProperty || ''}
                                    onChange={(e) => setFilterProperty(e.target.value)}
                                    displayEmpty
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: (selected) => {
                                            if (!selected) {
                                                return 'Todas';
                                            }
                                            const property = properties?.find((p) => p.id === selected);
                                            return property?.name || 'Todas';
                                        }
                                    }}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {properties?.map((property) => (
                                        <MenuItem key={property.id} value={property.id}>
                                            {property.name}
                                        </MenuItem>
                                    ))}
                                </TextFildStyled>
                            </Div>
                        </Grid>
                        <Grid item marginLeft={4} xs={2}>
                            <p>Consultor</p>
                            <Div width="100%">
                                <TextFildStyled
                                    fullWidth
                                    size="small"
                                    select
                                    value={filterConsultant || ''}
                                    onChange={(e) => setFilterConsultant(e.target.value)}
                                    displayEmpty
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: (selected) => {
                                            if (!selected) {
                                                return 'Todos';
                                            }
                                            const consultant = consultants?.find((c) => c.idUser === selected);
                                            return consultant?.name || 'Todos';
                                        }
                                    }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {consultants?.map((consultant) => (
                                        <MenuItem key={consultant.idUser} value={consultant.idUser}>
                                            {consultant.name}
                                        </MenuItem>
                                    ))}
                                </TextFildStyled>
                            </Div>
                        </Grid>
                    </ContainerFilter>
                    <Grid item padding={2}>
                        <CleanButton onClick={handleClean}>Limpar</CleanButton>
                    </Grid>
                    <Grid item padding={2}>
                        <FilterButton variant="contained" color="primary" onClick={handleFilterClick}>
                            Filtrar
                        </FilterButton>
                    </Grid>
                </GridFilter>
            </Grid>

            {/* Cards de Métricas */}
            <Grid container spacing={3}>
                {isLoading ? (
                    <Grid item xs={12}>
                        <LoadingComponent hideLogo />
                    </Grid>
                ) : (
                    averagesData.map((indicatorData) => {
                        const indicator = getIndicatorData(indicatorData);
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={2.4} key={indicatorData.report_indicator_id}>
                                <MetricCard
                                    title={indicator.name}
                                    value={indicator.value}
                                    unit={indicator.unit}
                                    goal={indicator.goal}
                                    percentage={indicator.percentage}
                                    isAboveGoal={indicator.isAboveGoal}
                                />
                            </Grid>
                        );
                    })
                )}
            </Grid>

            {/* Gráficos Dinâmicos */}
            <Grid container spacing={3} style={{ marginTop: '1rem' }}>
                {chartsData.map((chart) => (
                    <Grid item xs={12} md={6} key={chart.indicator_id}>
                        <Section>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <PaperTitle>
                                        {chart.chart_type === 'sum' ? 'Total' : 'Média'} {chart.indicator_name}{' '}
                                        {chart.indicator_measure && `(${chart.indicator_measure})`}
                                    </PaperTitle>
                                </Grid>
                                <Grid item xs={12}>
                                    <DynamicChart
                                        data={chart.data?.countPerMonth || []}
                                        isLoading={isChartLoading}
                                        indicatorName={chart.indicator_name}
                                    />
                                </Grid>
                            </Grid>
                        </Section>
                    </Grid>
                ))}
            </Grid>
        </>
    );
};

export default Goals;
